import { Link } from 'react-router-dom';
import { pricingTiers } from '../config/pricingTiers';
import { siteConfig } from '../config/siteConfig';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import SectionHeader from '../components/ui/SectionHeader';
import Card from '../components/ui/Card';
import './Homepage.css';

/* ── Demo leaderboard data ────────────────────────────────────
   Placeholder data to show visitors what the product looks like.
   Will be replaced with real Supabase data in Phase 2.
   ─────────────────────────────────────────────────────────── */
const leaderboardData = [
  { rank: 1, name: 'Marcus Chen',    rating: 1847, wins: 34, losses: 8,  change: '+12' },
  { rank: 2, name: 'Sarah Kim',      rating: 1821, wins: 29, losses: 9,  change: '+5'  },
  { rank: 3, name: 'James Okafor',   rating: 1795, wins: 31, losses: 12, change: '-3'  },
  { rank: 4, name: 'Priya Patel',    rating: 1762, wins: 22, losses: 10, change: '+8'  },
  { rank: 5, name: 'Tom Bradley',    rating: 1741, wins: 25, losses: 14, change: '+2'  },
  { rank: 6, name: 'Liu Wei',        rating: 1718, wins: 19, losses: 11, change: '-6'  },
  { rank: 7, name: 'Emma Rodriguez', rating: 1694, wins: 17, losses: 13, change: '+1'  },
];

function Homepage() {
  return (
    <main className="homepage">

      {/* ════════════════════════════════════════════════════
          HERO
          ════════════════════════════════════════════════════ */}
      <section className="hero">
        <div className="hero-glow" aria-hidden="true"></div>
        <div className="hero-inner">

          <Badge>Now in early access</Badge>

          <h1 className="hero-headline">
            The global standard for<br />
            <span className="hero-highlight">table tennis</span> ratings
          </h1>

          <p className="hero-subtext">{siteConfig.description}</p>

          <div className="hero-cta">
            <Button to="/signup" size="lg">Get started free</Button>
            <Button to="/clubs" variant="outline" size="lg">For clubs &rarr;</Button>
          </div>

          <p className="hero-proof">{siteConfig.socialProof}</p>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          VALUE PROPS
          ════════════════════════════════════════════════════ */}
      <section className="value-props" id="features">
        <div className="section-inner">
          <SectionHeader
            title="Everything you need to compete"
            subtitle="Professional-grade rating system, built for everyone."
          />
          <div className="props-grid">

            <Card className="prop-card">
              <div className="prop-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </div>
              <h3>Real ELO Ratings</h3>
              <p>The same algorithm used in chess — your rating rises when you beat stronger players and falls when you lose to weaker ones. Fair, accurate, automatic.</p>
            </Card>

            <Card className="prop-card">
              <div className="prop-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <h3>Club Leaderboards</h3>
              <p>Create a private leaderboard for your club, office, or friend group. Share it publicly or keep it internal — you control it.</p>
            </Card>

            <Card className="prop-card">
              <div className="prop-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10"/>
                  <line x1="12" y1="20" x2="12" y2="4"/>
                  <line x1="6" y1="20" x2="6" y2="14"/>
                </svg>
              </div>
              <h3>Track Your Progress</h3>
              <p>See your rating history over time, your win/loss record, and your toughest opponents. Know exactly where you stand.</p>
            </Card>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          LEADERBOARD PREVIEW
          ════════════════════════════════════════════════════ */}
      <section className="leaderboard-preview" id="leaderboard">
        <div className="section-inner">
          <SectionHeader
            title="See the leaderboard in action"
            subtitle="A preview of what your club's leaderboard looks like. Real ratings, live standings, updated after every match."
          />

          <div className="leaderboard-card">
            <div className="lb-card-header">
              <span className="club-label">Acme Corp — Table Tennis Club</span>
              <span className="live-badge">
                <span className="live-dot"></span>
                Live
              </span>
            </div>
            <table className="lb-table">
              <thead>
                <tr>
                  <th>#</th><th>Player</th><th>Rating</th>
                  <th>W</th><th>L</th><th>7d</th>
                </tr>
              </thead>
              <tbody>
                {leaderboardData.map((player) => (
                  <tr key={player.rank}>
                    <td className={`lb-rank lb-rank-${player.rank}`}>{player.rank}</td>
                    <td className="lb-name">{player.name}</td>
                    <td className="lb-rating">{player.rating}</td>
                    <td className="lb-wins">{player.wins}</td>
                    <td className="lb-losses">{player.losses}</td>
                    <td className={`lb-change ${player.change.startsWith('+') ? 'positive' : 'negative'}`}>
                      {player.change}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="preview-cta">
            <Button to="/signup">Create your club's leaderboard &rarr;</Button>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          PRICING
          Rendered from pricingTiers config — never hardcoded.
          ════════════════════════════════════════════════════ */}
      <section className="pricing" id="pricing">
        <div className="section-inner">
          <SectionHeader
            title="Simple, transparent pricing"
            subtitle="Start free. Upgrade when you're ready."
          />
          <div className="pricing-grid">
            {pricingTiers.map((tier) => (
              <Card
                key={tier.id}
                highlighted={tier.highlight}
                className="pricing-card"
              >
                {/* "Most popular" badge floats above the highlighted card */}
                {tier.highlight && (
                  <div className="popular-badge">Most popular</div>
                )}
                <div className="tier-name">{tier.name}</div>
                <div className="tier-price">
                  <span className="price-amount">{tier.price}</span>
                  <span className="price-period">/{tier.period}</span>
                </div>
                <p className="tier-desc">{tier.description}</p>
                <ul className="tier-features">
                  {tier.features.map((feature, i) => (
                    <li key={i}>
                      <span className="check-icon">&#10003;</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  to={tier.ctaHref}
                  variant={tier.highlight ? 'primary' : 'outline'}
                  fullWidth
                >
                  {tier.cta}
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          FINAL CTA
          ════════════════════════════════════════════════════ */}
      <section className="final-cta">
        <div className="section-inner final-cta-inner">
          <h2>Ready to find your ranking?</h2>
          <p>Join players across the world who are tracking their table tennis rating with RankTT.</p>
          <Button to="/signup" size="lg">Get started free &rarr;</Button>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          FOOTER
          ════════════════════════════════════════════════════ */}
      <footer className="footer">
        <div className="footer-inner">
          <Link to="/" className="footer-logo">Rank<span>TT</span></Link>
          <nav className="footer-links">
            <a href="/#features">Features</a>
            <a href="/#pricing">Pricing</a>
            <Link to="/clubs">For Clubs</Link>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-of-service">Terms of Service</Link>
          </nav>
          <p className="footer-copy">{siteConfig.copyright}</p>
        </div>
      </footer>

    </main>
  );
}

export default Homepage;
